import { readOdometer } from './obd/mode22.ts';
import type { Transport } from './transports/types.ts';
import { encoder } from './transports/types.ts';
import {
  bytesToDtc,
  describeDtc,
  hexToBytes,
  joinSegments,
  parseDtcResponse,
  parseMilStatus,
  splitResponses,
  type Dtc,
} from './obd/dtc.ts';
import { parseVinResponse } from './obd/vin.ts';
import { PIDS, parsePidResponse, pidNumber, pidRequest, type PidSpec, type Vitals } from './obd/pids.ts';
import {
  monitorName,
  parseCalibrationIds,
  parseCvns,
  parseEcuName,
  parseMonitorSupport,
  parseMonitorTests,
  parsePidSupport,
  parseReadiness,
  type MonitorTest,
  type Readiness,
  type VehicleInfo,
} from './obd/extended.ts';

// ELM327 command layer.
//
// The adapter is a single-threaded device with no request IDs, so the only way
// to know which reply belongs to which command is to send one at a time and
// wait for the '>' prompt. Everything here is built around that constraint:
// a strict queue, prompt-delimited framing, and per-command timeouts.

export interface LogEntry {
  at: number;
  direction: 'tx' | 'rx' | 'info' | 'error';
  text: string;
}

export interface Elm327Options {
  onLog?: (entry: LogEntry) => void;
  /** Default per-command timeout. ATZ and the first protocol probe get more. */
  timeoutMs?: number;
}

export interface ScanProgress {
  step: string;
  done: number;
  total: number;
}

/**
 * The snapshot the ECU stores at the instant a code sets.
 *
 * A code says what failed. The freeze frame says under what conditions, which
 * is usually the difference between a cold-start fault and a highway-cruise
 * fault with the same code and completely different cause lists.
 */
export interface FreezeFrame {
  /** The DTC that triggered the capture. */
  dtc: string | null;
  /** Frame index. Always 0 on production vehicles, but the protocol allows more. */
  frame: number;
  vitals: Vitals;
}

export interface ScanResult {
  vin: string | null;
  protocol: string | null;
  adapterId: string | null;
  milOn: boolean;
  reportedDtcCount: number;
  stored: Dtc[];
  pending: Dtc[];
  permanent: Dtc[];
  vitals: Vitals;
  freezeFrame: FreezeFrame | null;
  readiness: Readiness | null;
  monitorTests: (MonitorTest & { monitorLabel: string })[];
  vehicleInfo: VehicleInfo | null;
  /** Read from the vehicle, in kilometres. Null when it does not report one. */
  odometerKm: number | null;
  /** Every module that answered, so a disagreement can be shown rather than hidden. */
  odometerReadings: { header: string; km: number }[];
  odometerDisagreement: boolean;
  /** PIDs the vehicle said it supports, so we stop guessing. */
  supportedPids: number[];
  /** Battery voltage measured by the adapter, independent of PID 42. */
  adapterVoltage: number | null;
  rawLog: string;
  startedAt: string;
  finishedAt: string;
}

const PROMPT = '>';

/**
 * The adapter has gone, as opposed to a command that went unanswered.
 *
 * Worth its own type because every read in a scan tolerates an empty reply -
 * plenty of PIDs are unsupported and that is normal. A closed transport is not
 * normal, and swallowing it the same way is what let a scan carry on issuing
 * commands into a dead link and then report success.
 */
export class AdapterGoneError extends Error {
  constructor(message = 'The adapter disconnected during the scan.') {
    super(message);
    this.name = 'AdapterGoneError';
  }
}

/**
 * Whether a reply is a negative response for the given service.
 *
 * 7F <service> <code>, where 0x11 is "service not supported" and 0x12 is
 * "subfunction not supported". Both mean stop asking; anything else is a
 * transient condition worth retrying.
 *
 * Multiple ECUs can answer, so this looks anywhere in the reply rather than
 * only at the start.
 */
export function isNotSupported(raw: string, service: number): boolean {
  if (!raw) return false;
  const hex = raw.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  const svc = service.toString(16).toUpperCase().padStart(2, '0');
  return hex.includes(`7F${svc}11`) || hex.includes(`7F${svc}12`);
}

export class Elm327 {
  private transport: Transport;
  private opts: Elm327Options;

  private rxBuffer = '';
  private pending: {
    resolve: (value: string) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  private unsubscribe: (() => void) | null = null;
  private log: LogEntry[] = [];

  /**
   * How many modules answer a request, learned from the protocol probe.
   *
   * An ELM327 has no way to know a conversation is finished, so it waits out
   * its timeout after the last reply before printing the prompt. Telling it how
   * many replies to expect lets it return the instant they arrive, which on a
   * two-module truck roughly doubles the polling rate. Wrong guesses truncate
   * data, so this is only used for live polling and is abandoned on the first
   * read it breaks.
   */
  private responderCount: number | null = null;

  constructor(transport: Transport, opts: Elm327Options = {}) {
    this.transport = transport;
    this.opts = opts;
  }

  get transportKind() {
    return this.transport.kind;
  }

  private record(direction: LogEntry['direction'], text: string) {
    const entry = { at: Date.now(), direction, text };
    this.log.push(entry);
    if (this.log.length > 500) this.log.shift();
    this.opts.onLog?.(entry);
  }

  rawLog(): string {
    return this.log.map((e) => `${e.direction.toUpperCase().padEnd(5)} ${e.text}`).join('\n');
  }

  private attach() {
    if (this.unsubscribe) return;
    const decoderStream = new TextDecoder();
    this.unsubscribe = this.transport.onData((chunk) => {
      this.rxBuffer += decoderStream.decode(chunk, { stream: true });
      // The prompt is the only reliable end-of-response marker.
      const promptAt = this.rxBuffer.indexOf(PROMPT);
      if (promptAt < 0 || !this.pending) return;

      const response = this.rxBuffer.slice(0, promptAt);
      this.rxBuffer = this.rxBuffer.slice(promptAt + 1);

      const { resolve, timer } = this.pending;
      this.pending = null;
      clearTimeout(timer);

      const cleaned = response.trim();
      this.record('rx', cleaned.replace(/\r/g, ' | ') || '(empty)');
      resolve(cleaned);
    });
  }

  /** Send one command and wait for its prompt. Serialised against every other call. */
  /**
   * A read whose failure is acceptable. An unanswered command yields '', which
   * every parser already treats as "not supported". A lost adapter is rethrown,
   * because nothing after it can succeed.
   */
  private async tryCommand(cmd: string, timeoutMs?: number): Promise<string> {
    try {
      return await this.command(cmd, timeoutMs);
    } catch (err) {
      if (err instanceof AdapterGoneError) throw err;
      return '';
    }
  }

  command(cmd: string, timeoutMs = this.opts.timeoutMs ?? 5_000): Promise<string> {
    const run = async (): Promise<string> => {
      if (!this.transport.isOpen) throw new AdapterGoneError();
      this.attach();

      // Anything still buffered belongs to a previous command.
      this.rxBuffer = '';
      this.record('tx', cmd);

      const result = new Promise<string>((resolve, reject) => {
        const timer = setTimeout(() => {
          this.pending = null;
          const err = new Error(`No prompt from the adapter within ${timeoutMs}ms after "${cmd}".`);
          this.record('error', err.message);
          reject(err);
        }, timeoutMs);
        this.pending = { resolve, reject, timer };
      });

      await this.transport.write(encoder.encode(cmd + '\r'));
      const raw = await result;
      return stripEcho(raw, cmd);
    };

    // Chain onto the queue so overlapping callers cannot interleave commands.
    const chained = this.queue.then(run, run);
    this.queue = chained.catch(() => undefined);
    return chained;
  }

  /** Reset the adapter and negotiate a protocol with the vehicle. */
  async connect(): Promise<{ adapterId: string | null; protocol: string | null }> {
    if (!this.transport.isOpen) await this.transport.open();

    // ATZ is a full reset and is slow; everything after it is fast.
    const idRaw = await this.tryCommand('ATZ', 8_000);
    const adapterId = idRaw.split(/[\r\n]+/).map((s) => s.trim()).filter(Boolean).pop() ?? null;
    this.record('info', `Adapter identified as ${adapterId ?? 'unknown'}`);

    // Echo off first, then the formatting settings the parsers assume.
    await this.command('ATE0');
    await this.command('ATL0'); // no extra linefeeds
    await this.command('ATS0'); // no spaces between bytes
    await this.command('ATH0'); // no headers
    await this.command('ATSP0'); // automatic protocol search

    // The first real request is what actually triggers protocol negotiation,
    // so it needs a long timeout and its failure is meaningful.
    const probe = await this.command('0100', 12_000);
    if (/UNABLE TO CONNECT|NO DATA|BUS INIT.*ERROR|CAN ERROR/i.test(probe)) {
      throw new Error(
        'The adapter is talking but the vehicle is not answering. Turn the ignition to ON (engine can be off) and make sure the adapter is fully seated in the OBD port.',
      );
    }

    const responders = splitResponses(probe).length;
    this.responderCount = responders > 0 && responders < 10 ? responders : null;
    if (this.responderCount) this.record('info', `${this.responderCount} module(s) answering`);

    const protocol = (await this.tryCommand('ATDP')).trim() || null;
    // Anything that is not CAN uses the six-byte mode 06 record.
    this.legacyMode06 = !/CAN|15765/i.test(protocol ?? '');
    this.record('info', `Protocol: ${protocol ?? 'unknown'}${this.legacyMode06 ? ' (legacy mode 06)' : ''}`);
    return { adapterId, protocol };
  }

  /**
   * One PID, as fast as the adapter will give it up. Used by live polling.
   *
   * Falls back to an un-hinted request the first time the responder count
   * produces nothing, and stops hinting from then on.
   */
  async readVital(spec: PidSpec): Promise<number | null> {
    const hint = this.responderCount ? String(this.responderCount) : '';
    const value = parsePidResponse(await this.command(pidRequest(spec, 1) + hint, 2_500), spec, 1);
    if (value !== null || !hint) return value;

    this.responderCount = null;
    this.record('info', 'Response-count hint rejected; falling back to plain requests');
    return parsePidResponse(await this.command(pidRequest(spec, 1), 2_500), spec, 1);
  }

  async readVin(): Promise<string | null> {
    const raw = await this.command('0902', 8_000);
    const vin = parseVinResponse(raw);
    this.record('info', vin ? `VIN ${vin}` : 'VIN not reported by this vehicle');
    return vin;
  }

  async readMilStatus(): Promise<{ milOn: boolean; dtcCount: number }> {
    const raw = await this.command('0101');
    return parseMilStatus(raw) ?? { milOn: false, dtcCount: 0 };
  }

  async readDtcs(): Promise<{ stored: Dtc[]; pending: Dtc[]; permanent: Dtc[] }> {
    const stored = parseDtcResponse(await this.command('03', 8_000), 'stored', 0x03);
    const pending = parseDtcResponse(await this.command('07', 8_000), 'pending', 0x07);
    // Mode 0A is OBD-II 2010+ only; older vehicles answer NO DATA, which is fine.
    const permanent = parseDtcResponse(await this.tryCommand('0A', 8_000), 'permanent', 0x0a);
    return { stored, pending, permanent };
  }

  /**
   * Ask the vehicle which PIDs it answers, one 32-bit block at a time.
   *
   * The last bit of each block is the "there is another block" flag, so the
   * walk stops naturally rather than probing ranges that do not exist.
   */
  async readSupportedPids(): Promise<number[]> {
    const found: number[] = [];
    for (const base of [0x00, 0x20, 0x40, 0x60, 0x80]) {
      const cmd = '01' + base.toString(16).toUpperCase().padStart(2, '0');
      const raw = await this.tryCommand(cmd, 5_000);
      const { pids, hasNextBlock } = parsePidSupport(raw, base);
      found.push(...pids);
      if (!hasNextBlock) break;
    }
    this.supportedPids = new Set(found);
    this.record('info', `Vehicle supports ${found.length} data channels`);
    return found;
  }

  private supportedPids: Set<number> | null = null;

  /** True when support is unknown, so an undiscovered vehicle still gets read. */
  private isSupported(spec: PidSpec): boolean {
    return this.supportedPids === null || this.supportedPids.has(pidNumber(spec));
  }

  async readVitals(): Promise<Vitals> {
    const vitals: Vitals = {};
    for (const spec of PIDS) {
      if (!this.isSupported(spec)) {
        // Skipping is not the same as failing, and the UI distinguishes them.
        vitals[spec.key] = null;
        continue;
      }
      try {
        vitals[spec.key] = parsePidResponse(await this.command(pidRequest(spec, 1), 3_000), spec, 1);
      } catch {
        vitals[spec.key] = null;
      }
    }
    return vitals;
  }

  /** Readiness monitors, the part of PID 01 every code reader discards. */
  async readReadiness(): Promise<Readiness | null> {
    const readiness = parseReadiness(await this.tryCommand('0101', 5_000));
    if (readiness) {
      this.record(
        'info',
        `${readiness.ignition} ignition, ${readiness.incompleteCount} monitor(s) incomplete`,
      );
    }
    return readiness;
  }

  /**
   * Mode 06 test results. Enumerates supported monitor ids, then reads each.
   *
   * Plenty of vehicles answer nothing here, which is not an error worth
   * surfacing; it just means this one keeps its measurements to itself.
   */
  /** Set at connect: pre-CAN protocols use a different mode 06 record. */
  private legacyMode06 = false;

  async readMonitorTests(): Promise<(MonitorTest & { monitorLabel: string })[]> {
    const ids: number[] = [];
    for (const base of [0x00, 0x20, 0x40, 0x60, 0x80]) {
      const cmd = '06' + base.toString(16).toUpperCase().padStart(2, '0');
      const raw = await this.tryCommand(cmd, 5_000);

      // A refusal is an answer. 7F 06 11 is "service not supported", and a
      // 2023 F-250 says exactly that - yet the walk carried on asking for
      // thirty more test IDs the vehicle had already declined, and the adapter
      // answered the first and then timed out on every one after.
      //
      // Fifteen seconds of a scan were spent confirming a "no" we had been
      // given in the first reply.
      if (isNotSupported(raw, 0x06)) {
        this.record('info', 'This vehicle does not support mode 06 (on-board test results).');
        return [];
      }

      const { pids, hasNextBlock } = parseMonitorSupport(raw, base);
      ids.push(...pids);
      if (!hasNextBlock) break;
    }

    const results: (MonitorTest & { monitorLabel: string })[] = [];

    // Mode 06 is where cheap BLE adapters give up. The replies are the largest
    // in a scan - a single TID can reassemble to over a hundred bytes across
    // eight segments - and after one of those some adapters stop answering
    // entirely.
    //
    // So this stops asking after two silent TIDs in a row. One silence is
    // ordinary, since not every advertised TID returns data. Two means the
    // adapter has stopped talking, and grinding through thirty more requests
    // it will not answer just delays the scan and gives the link more time to
    // fail outright.
    let silent = 0;

    for (const id of ids.slice(0, 32)) {
      const cmd = '06' + id.toString(16).toUpperCase().padStart(2, '0');
      const raw = await this.tryCommand(cmd, 5_000);

      if (isNotSupported(raw, 0x06)) {
        this.record('info', 'Mode 06 refused partway through; stopping.');
        break;
      }

      if (!raw) {
        silent += 1;
        if (silent >= 2) {
          this.record('info', 'Adapter stopped answering mode 06; skipping the rest.');
          break;
        }
        continue;
      }
      silent = 0;

      const parsed = parseMonitorTests(raw, this.legacyMode06);
      for (const test of parsed) {
        results.push({ ...test, monitorLabel: monitorName(test.monitorId) });
      }

      // Let a small buffer drain before the next request. Nothing else in a
      // scan produces back-to-back transfers this size.
      if (parsed.length > 0) await new Promise((r) => setTimeout(r, 60));
    }

    if (results.length > 0) this.record('info', `${results.length} on-board test result(s)`);
    return results;
  }

  /** Calibration identity: what software this ECU is actually running. */
  async readVehicleInfo(): Promise<VehicleInfo | null> {
    const calRaw = await this.tryCommand('0904', 6_000);
    const cvnRaw = await this.tryCommand('0906', 6_000);
    const nameRaw = await this.tryCommand('090A', 6_000);

    const info: VehicleInfo = {
      calibrationIds: parseCalibrationIds(calRaw),
      calibrationVerificationNumbers: parseCvns(cvnRaw),
      ecuName: parseEcuName(nameRaw),
    };

    const anything = info.calibrationIds.length || info.calibrationVerificationNumbers.length || info.ecuName;
    if (anything) this.record('info', `Calibration ${info.calibrationIds.join(', ') || '(unnamed)'}`);
    return anything ? info : null;
  }

  /** The adapter's own voltage reading, a sanity check against PID 42. */
  async readAdapterVoltage(): Promise<number | null> {
    const raw = await this.tryCommand('ATRV', 3_000);
    const match = /(\d+\.?\d*)\s*V/i.exec(raw);
    return match ? Number(match[1]) : null;
  }

  /**
   * Read the freeze frame, if the vehicle has one stored.
   *
   * Mode 02 PID $02 reports which DTC owns the frame, so it doubles as the
   * cheapest possible probe: no frame means no answer here, and we skip the
   * seven follow-up reads entirely.
   */
  async readFreezeFrame(): Promise<FreezeFrame | null> {
    const raw = await this.tryCommand('020200', 5_000);
    const hex = joinSegments(raw);
    const at = hex.indexOf('4202');
    if (at < 0) {
      this.record('info', 'No freeze frame stored');
      return null;
    }

    // 42 02 <frame> <dtc high> <dtc low>
    const bytes = hexToBytes(hex.slice(at + 4));
    if (bytes.length < 3) return null;

    const frame = bytes[0];
    const dtc = bytesToDtc(bytes[1], bytes[2]);
    if (!dtc) {
      // 00 00 in the DTC slot means the frame was allocated but never filled.
      this.record('info', 'Freeze frame slot is empty');
      return null;
    }

    const vitals: Vitals = {};
    for (const spec of PIDS) {
      try {
        vitals[spec.key] = parsePidResponse(await this.command(pidRequest(spec, 2), 3_000), spec, 2);
      } catch {
        vitals[spec.key] = null;
      }
    }

    this.record('info', `Freeze frame captured when ${dtc} set`);
    return { dtc, frame, vitals };
  }

  /**
   * Clear stored codes and turn off the lamp.
   *
   * This also erases freeze-frame data and resets readiness monitors, which
   * can fail an emissions test until the drive cycle completes. The UI says so
   * before calling this.
   */
  async clearCodes(): Promise<void> {
    const raw = await this.command('04', 8_000);
    if (/NO DATA|ERROR|UNABLE/i.test(raw)) {
      throw new Error('The vehicle refused the clear request. Some modules only accept it with the engine off.');
    }
    this.record('info', 'Codes cleared');
  }

  /** The whole read, in the order a scan tool would do it. */
  async runFullScan(onProgress?: (p: ScanProgress) => void): Promise<ScanResult> {
    const startedAt = new Date().toISOString();
    const steps = [
      'Connecting',
      'Discovering channels',
      'Reading VIN',
      'Checking readiness',
      'Reading codes',
      'Reading live data',
      'Reading freeze frame',
      'Reading test results',
      'Reading calibration',
    ];
    const step = (i: number) => onProgress?.({ step: steps[i], done: i, total: steps.length });

    step(0);
    const { adapterId, protocol } = await this.connect();

    step(1);
    const supportedPids = await this.readSupportedPids().catch(() => [] as number[]);

    step(2);
    const vin = await this.readVin().catch(() => null);

    step(3);
    const readiness = await this.readReadiness().catch(() => null);
    const mil = readiness
      ? { milOn: readiness.milOn, dtcCount: readiness.dtcCount }
      : await this.readMilStatus().catch(() => ({ milOn: false, dtcCount: 0 }));

    step(4);
    const { stored, pending, permanent } = await this.readDtcs();

    step(5);
    const vitals = await this.readVitals();

    step(6);
    const freezeFrame = await this.readFreezeFrame().catch(() => null);

    // Asked of every vehicle, not just Fords. The identifier is verified on
    // Ford and unproven elsewhere, but a make that answers is a make that
    // works, and gating it by VIN guaranteed no mileage on anything else.
    //
    // The guard moved to where it belongs: mode22 refuses to believe a reply
    // that is not plausible as a road distance, and refuses a pair of modules
    // that disagree. Asking is free; believing is what needs care.
    //
    // Read before mode 06, not after. Mode 06 is where cheap clones give up:
    // on a real F-250 the adapter answered 0601, timed out on 0602 and stopped
    // responding entirely. The odometer read then inherited a dead adapter and
    // reported "no module answered DD01" - true, and completely misleading.
    let odometer: Awaited<ReturnType<typeof readOdometer>> | null = null;
    {
      try {
        odometer = await readOdometer((cmd: string, timeoutMs?: number) => this.command(cmd, timeoutMs));
        this.record(
          'info',
          odometer.km != null
            ? `odometer ${odometer.km} km from ${odometer.readings.map((r) => r.header).join(', ')}`
            : odometer.refused
              ? 'odometer: this vehicle does not support mode 22'
              : 'odometer: no module answered DD01',
        );
      } catch (err) {
        // Reported, not swallowed. Catching this to null made a failure
        // indistinguishable from a vehicle that simply does not answer, which
        // is the difference between a bug and a fact about the car.
        odometer = null;
        this.record('error', `odometer read failed: ${(err as Error).message}`);
      }
    }

    step(8);
    const vehicleInfo = await this.readVehicleInfo().catch(() => null);
    const adapterVoltage = await this.readAdapterVoltage().catch(() => null);

    // Mode 06 last, because it is the least useful thing here and the most
    // reliable way to break the session. Two vehicles in a row answered 0601
    // and then stopped responding entirely: on the F-250 that cost the
    // odometer, and on a BMW it killed the mode 09 read that follows, so a scan
    // lost its vehicle information to an optional extra.
    //
    // Anything that fails after this point was going to be lost anyway.
    step(7);
    const monitorTests = await this.readMonitorTests().catch(() => []);

    onProgress?.({ step: 'Done', done: steps.length, total: steps.length });

    return {
      vin,
      protocol,
      adapterId,
      odometerKm: odometer?.km ?? null,
      odometerReadings: odometer?.readings ?? [],
      odometerDisagreement: odometer?.disagreement ?? false,
      milOn: mil.milOn,
      reportedDtcCount: mil.dtcCount,
      stored,
      pending,
      permanent,
      vitals,
      freezeFrame,
      readiness,
      monitorTests,
      vehicleInfo,
      supportedPids,
      adapterVoltage,
      rawLog: this.rawLog(),
      startedAt,
      finishedAt: new Date().toISOString(),
    };
  }

  async disconnect() {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.pending) {
      clearTimeout(this.pending.timer);
      this.pending.reject(new Error('Disconnected while a command was in flight.'));
      this.pending = null;
    }
    await this.transport.close();
  }
}

/** All DTCs from a scan, flattened and de-duplicated across the three modes. */
export function allDtcs(result: Pick<ScanResult, 'stored' | 'pending' | 'permanent'>): Dtc[] {
  const seen = new Map<string, Dtc>();
  for (const d of [...result.stored, ...result.pending, ...result.permanent]) {
    // Stored wins over pending wins over permanent when a code appears twice.
    if (!seen.has(d.code)) seen.set(d.code, d);
  }
  return [...seen.values()];
}

export { describeDtc };

/**
 * Strip the echoed command from a response.
 *
 * ATE0 turns echo off, but it cannot apply to itself or to the ATZ before it,
 * so the first two responses of every session arrive with the command attached.
 */
function stripEcho(raw: string, cmd: string): string {
  const lines = raw.split(/[\r\n]+/);
  if (lines[0]?.trim().replace(/\s+/g, '').toUpperCase() === cmd.replace(/\s+/g, '').toUpperCase()) {
    return lines.slice(1).join('\r').trim();
  }
  return raw;
}
